import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import firebase from 'firebase/app';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import Toolbar from '@mui/material/Toolbar';
import Divider from '@mui/material/Divider';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';

import EnhancedTableHead from '../../components/EnhancedTableHead';
import EnhancedTableRow from '../../components/EnhancedTableRow';
import { getComparator, stableSort } from '../../modules/sort';
import SearchBox from '../../components/SearchBox';
import ContextStore from '../../modules/context';
import { useFirestoreDataAndMapping, unwrap } from '../../modules/uitls';
import { ProductCategories as productCategories } from '../../constants/index';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 12 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const fields = [
  { name: 'nickname', sm: 6 },
  { name: 'type', type: 'select', sm: 6 },
  { name: 'consumable', sm: 6 },

  { name: 'cost', sm: 6 },
  { name: 'staffprice', sm: 6 },
  { name: 'staffRelativesPrice', sm: 6 },
  { name: 'vipprice', sm: 6 },
  { name: 'note', type: 'multiline', multiline: true, sm: 12, md: 12 },
  { name: 'owner', type: '-' },

  { name: 'price', sm: 6 },
  { name: 'lowestPrice', sm: 6 },
  { name: 'duration', sm: 6 },
  { name: 'operationBonus', sm: 6 },

  { name: 'doctorBonusType', type: 'select', sm: 6 },
  { name: 'bonusType', type: 'select', sm: 6 },
  { name: 'salesBonusType', type: 'select', sm: 6 },

  // { name: 'onShelfDate', sm: 6, md: 3 },
  // { name: 'offShelfDate', sm: 6, md: 3 },

  { name: 'description', type: 'multiline', multiline: true, sm: 12, md: 12 },

].map(field => { field.multiline = field.multiline || false; field.md = field.md || 3; return field })

function ProductDetail({ allowEditing, customerMapping, product, customerId, productMapping }) {
  const { formatMessage } = useIntl()
  const openCode = product.cat.substr(0, 1)
  const [merchandiseMapping] = useFirestoreDataAndMapping('merchandises')
  const selectMapping = {
    type: productCategories.reduce((acc, cur) => { acc[cur] = formatMessage({ id: `product.type.${cur}` }); return acc }, {}),
    bonusType: [
      'none', 'purchaseOrder', 'treatment'
    ].reduce((acc, cur) => { acc[cur] = formatMessage({ id: `product.bonusType.${cur}` }); return acc }, {}),
    doctorBonusType: [
      'none', 'laser', 'microShaping', 'lift', 'bodyFiguring', 'instalift', 'noImplantOp', 'other'
    ].reduce((acc, cur) => { acc[cur] = formatMessage({ id: `product.doctorBonusType.${cur}` }); return acc }, {}),
    salesBonusType: [
      'none', 'a', 'b', 'c'
    ].reduce((acc, cur) => { acc[cur] = formatMessage({ id: `product.salesBonusType.${cur}` }); return acc }, {}),
  }

  const headerCells = [
    { text: 'nickname' },
    { text: 'type' },
    { text: 'total' },
  ].map(c => { c.text = formatMessage({ id: `product.merchandise.detail.${c.text}` }); return c })

  const rowCells = [
    { field: 'nickname', tooltip: 'name' },
    { field: 'type' },
    { field: 'total' },
  ]

  const headerPriceCells = [
    { text: 'name' },
    { text: 'useAmount' },
    { text: 'price' },
    { text: 'fixedAmount' },
    { text: 'onShelfDate' },
    { text: 'offShelfDate' },
  ].map(c => { c.text = formatMessage({ id: `product.commonPrice.${c.text}` }); return c })

  const rowPriceCells = [
    { field: 'name' },
    { field: 'useAmount' },
    { field: 'price' },
    { field: 'fixedAmount' },
    { field: 'onShelfDate' },
    { field: 'offShelfDate' },
  ]

  const headerProductCells = [
    { text: 'code' },
    { text: 'name' },
    { text: 'points' },
  ].map(c => { c.text = formatMessage({ id: `product.convertibleProduct.${c.text}` }); return c })

  const rowProductCells = [
    { field: 'code' },
    { field: 'name' },
    { field: 'points' },
  ]

  const _packageHeaderCells = [
    { text: 'name', order: 0 },
    { text: 'amount', order: 1 },
    { text: 'customers', order: 4 },
    { text: 'cost', order: 5 },
  ]

  const _packageRowCells = [
    { field: 'name', order: 0 },
    { field: 'amount', order: 1 },
    { field: 'customers', order: 4 },
    { field: 'cost', order: 5 },
  ]

  if (openCode !== '7') {
    _packageHeaderCells.push({ text: 'ratio', order: 2 })
    _packageRowCells.push({ field: 'ratio', order: 2 })
  } else {
    _packageHeaderCells.push({ text: 'price', order: 2 }, { text: 'lowestPrice', order: 3 })
    _packageRowCells.push({ field: 'price', order: 2 }, { field: 'lowestPrice', order: 3 })
  }

  const packageHeaderCells = _packageHeaderCells.map(c => { c.text = formatMessage({ id: `product.product.${c.text}` }); return c }).sort((a, b) => a.order - b.order)
  const packageRowCells = _packageRowCells.sort((a, b) => a.order - b.order)

  const formatData = (merchandise) => {
    const newData = { ...merchandise }
    newData.nickname = merchandiseMapping[newData.id].nickname
    newData.name = merchandiseMapping[newData.id].name
    newData.type = formatMessage({ id: `product.merchandise.type.${newData.type}` })

    return newData
  }

  const packageFormatData = (product) => {
    const newData = { ...product }
    const customers = Object.keys(product.customers || []).filter(c => customerMapping[c])
    newData.customers = customers.map(s => customerMapping[s]).map(s => s.nickname).join(', ')
    newData.tags = (product.tags || []).join(', ')

    return newData
  }

  function createField(field, value, product) {
    let newValue = value ?? ''
    if (!['8', '1', '9'].includes(openCode)) {
      if (['bonusType', 'doctorBonusType', 'salesBonusType', 'operationBonus'].includes(field.name)) {
        return
      }
    } else if (['9'].includes(openCode)) {
      if (['operationBonus'].includes(field.name)) {
        return
      }
    }

    if (field.type === 'select') {
      if (['type', 'bonusType', 'doctorBonusType'].includes(field.name)) {
        newValue = selectMapping[field.name][value]
      } else if (field.name === 'salesBonusType') {
        newValue = value?.name
      } else {
        // console.log(product.type, value)
        newValue = selectMapping[field.name][product.type][value]
      }
    }

    if (field.type === '-') {
      return <Grid item key={field.name} xs={12} sm={field.sm} md={12}><Divider /></Grid>
    }

    return <Grid item key={field.name} xs={12} sm={field.sm} md={field.md}>
      <TextField
        multiline={field.multiline}
        type="text"
        label={formatMessage({ id: `product.table.detail.${field.name}` })}
        value={field.name === 'consumable' ? value ? '療程消耗' : '直接銷售' : newValue}
        fullWidth
        size="small"
        variant="standard"
        readOnly
      />
    </Grid>
  }

  const newConvertibleProducts = product.convertibleProducts ? Object.keys(product.convertibleProducts).reduce((acc, cur) => {
    acc.push({ id: cur, name: productMapping[cur].name, code: productMapping[cur].code, points: product.convertibleProducts[cur] })
    return acc
  }, []) : []
  return (
    <div style={{ padding: 15 }}>
      <Grid container spacing={2}>
        {fields.map(field => createField(field, product[field.name], product))}
        {product.commonPrice && <>
          <div style={{ paddingLeft: '8px', marginTop: '8px' }}>
            <Typography variant="button" component="div">
              <FormattedMessage id="product.commonPrice.title" />:
            </Typography>
          </div>
          <TableContainer style={{ marginBottom: '8px' }} component={Paper}>
            <Table size="small" aria-label="collapsible table">
              <EnhancedTableHead
                headerCells={headerPriceCells}
                rowCount={product.commonPrice.length}
              />
              <TableBody>
                {product.commonPrice.map(commonPrice => (
                  <EnhancedTableRow
                    key={commonPrice.id}
                    rowCells={rowPriceCells}
                    cellData={commonPrice}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>}
        {product.convertibleProducts && <>
          <div style={{ paddingLeft: '8px', marginTop: '8px' }}>
            <Typography variant="button" component="div">
              <FormattedMessage id="product.convertibleProduct.title" />:
            </Typography>
          </div>
          <TableContainer style={{ marginBottom: '8px' }} component={Paper}>
            <Table size="small" aria-label="collapsible table">
              <EnhancedTableHead
                headerCells={headerProductCells}
                rowCount={newConvertibleProducts.length}
              />
              <TableBody>
                {newConvertibleProducts.map(product => (
                  <EnhancedTableRow
                    key={product.id}
                    rowCells={rowProductCells}
                    cellData={product}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>}
        {openCode !== '1' && <>
          <div style={{ paddingLeft: '8px', marginTop: '8px' }}>
            <Typography variant="button" component="div">
              <FormattedMessage id="product.products" />:
            </Typography>
          </div>
          <TableContainer style={{ marginBottom: '8px' }} component={Paper}>
            <Table size="small" aria-label="collapsible table">
              <EnhancedTableHead
                headerCells={packageHeaderCells}
                rowCount={product.package.length}
              />
              <TableBody>
                {product.package.map(m => packageFormatData(m)).map(packageProduct => (
                  <EnhancedTableRow
                    key={packageProduct.id}
                    rowCells={packageRowCells}
                    cellData={packageProduct}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>}
        {product.merchandiseType.length > 0 && <>
          <div style={{ paddingLeft: '8px', marginTop: '8px' }}>
            <Typography variant="button" component="div">
              <FormattedMessage id="product.merchandises" />:
            </Typography>
          </div>
          <TableContainer style={{ marginBottom: '8px' }} component={Paper}>
            <Table size="small" aria-label="collapsible table">
              <EnhancedTableHead
                headerCells={headerCells}
                rowCount={product.merchandiseType.length}
              />
              <TableBody>
                {product.merchandiseType.map(m => formatData(m)).map(merchandise => (
                  <EnhancedTableRow
                    key={merchandise.id}
                    rowCells={rowCells}
                    cellData={merchandise}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>}
        {allowEditing && <Grid item key="buttons" xs={12} sm={12} md={12}>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
            <Link to={`/products/${product.id}/customer/${customerId}`} style={{ textDecoration: 'none', color: '#000' }}>
              <Button variant="contained" color="primary">
                <FormattedMessage id="button.edit" />
              </Button>
            </Link>
          </div>
        </Grid>}
      </Grid>
    </div>
  )
}

ProductDetail.propTypes = {
  product: PropTypes.object.isRequired,
  customerMapping: PropTypes.object.isRequired,
  customerId: PropTypes.string.isRequired,
  productMapping: PropTypes.object.isRequired,
  allowEditing: PropTypes.bool.isRequired,
};

function SyncProductView({ customerId, productMapping }) {
  const { formatMessage } = useIntl()
  const { setBreadcrumbs, currentUser } = useContext(ContextStore)
  const location = useLocation()
  const [productData, setProductData] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('code');
  const [currentFilter, setCurrentFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState({ tags: [] });
  const [customerMapping] = useFirestoreDataAndMapping('customers')

  useEffect(() => {
    const unsubscribe = firebase.firestore().collection('syncProducts').doc(customerId).collection('productMapping').onSnapshot(snapshot => {
      const data = []
      snapshot.forEach(doc => {
        data.push({ id: doc.id, ...unwrap(doc.data()) })
      })

      setProductData(data)
    }, err => { })

    return () => unsubscribe()
  }, []);

  useEffect(() => {
    setBreadcrumbs([{
      text: formatMessage({ id: 'sideMenu.product.customer' })
    }])
    return () => {
    };
  }, [location.pathname]);

  const products = productData.filter(i => productMapping[i.id]).map(i => ({ ...i, ...productMapping[i.id], merchandiseType: merchandiseConcat(i) }))

  const filteredproducts = filterproducts()
  const filteredTags = [...new Set(
    (categoryFilter.type ?  products.filter(p => p.type.toLowerCase().includes(categoryFilter.type.toLowerCase())) : products).reduce((acc, cur) => acc.concat(cur.tags || []), [])
  )].sort()

  const headerCells = [
    { text: 'code', sort: 'code' },
    { text: 'name' },
    { text: 'nickname' },
    { text: 'onShelfDate' },
    { text: 'offShelfDate' }
  ].map(c => { c.text = formatMessage({ id: `product.table.header.${c.text}` }); return c })

  const rowCells = [
    { field: 'code' },
    { field: 'name' },
    { field: 'nickname' },
    { field: 'onShelfDate' },
    { field: 'offShelfDate' },
  ]

  const formatData = (product) => {
    const newData = { ...product }
    const newPackage = []
    const newCommonPrice = []

    if (newData.commonPrice) {
      for (const x of Object.keys(newData.commonPrice)) {
        newCommonPrice.push({ id: x, ...newData.commonPrice[x] })
      }

      newData.commonPrice = newCommonPrice
    }

    if (newData.package) {
      for (const p of Object.keys(newData.package)) {
        newPackage.push({
          id: p,
          ...newData.package[p],
          name: productMapping[p].name,
          customers: productMapping[p].customers,
          cost: productMapping[p].cost
        })
      }

      newData.package = newPackage
    }

    return newData
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filterItems = [
    { name: 'code' },
    { name: 'name', default: true },
    { name: 'nickname' }
  ].map(i => { i.text = formatMessage({ id: `product.table.detail.${i.name}` }); return i })

  const categoryFilterItems = [
    { name: 'type' },
    { name: 'tags' },
  ].map(i => { i.text = formatMessage({ id: `product.table.detail.${i.name}` }); return i })

  function filterproducts() {
    if (!currentFilter && !categoryFilter.type && categoryFilter.tags.length === 0) {
      return products
    }
    let items = [...products]
    if (categoryFilter.type) {
      const type = categoryFilter.type.toUpperCase()
      items = items.filter(i => i.type === type)
    }
    if (categoryFilter.tags.length > 0) {
      items = items.filter(i => categoryFilter.tags.every(tag => (i.tags || []).includes(tag)))
    }
    if (currentFilter) {
      items = items.filter(s => s[currentFilter.name].toLowerCase().includes(currentFilter.text.toLowerCase()))
    }
    return items
  }

  const onFilterChanged = (name, text) => {
    if (text !== '') {
      setCurrentFilter({ name, text })
    } else {
      setCurrentFilter(null)
    }
  }

  function getMenuItem(name) {
    if (name === 'type') {
      return [
        { value: '' },
        { value: 'beauty', header: true }, { value: 'AM' }, { value: 'OP' }, { value: 'SK' }, { value: 'DP' },
        { value: 'preventative', header: true }, { value: 'CN' }, { value: 'EX' }, { value: 'TT' }, { value: 'GE' }, { value: 'SU' },
        { value: 'ND' }, { value: 'CU' },
        { value: 'other', header: true }, { value: 'OT' }
      ].map(i => {
        i.label = i.value ? formatMessage({ id: `product.type.${i.value}` }) : formatMessage({ id: 'product.category.all' })
        return i
      })
    }
    return []
  }

  function merchandiseConcat(product) {

    const merchandises = Object.keys(product.merchandises || {}).map(key => ({
      ...product.merchandises[key],
      id: key,
      type: 'merchandises'
    }))

    const adjustables = Object.keys(product.adjustables || {}).map(key => ({
      ...product.adjustables[key],
      id: key,
      type: 'adjustables'
    }))

    return merchandises.concat(adjustables)
  }

  function updateCategoryFilter(name, value) {
    const newFilter = { ...categoryFilter }
    newFilter[name] = value
    setCategoryFilter(newFilter)
  }

  const getFilter = (filter) => {
    if (filter.name === 'tags') {
      return <Grid item key={filter.name} xs={3} sm={3} md={6}>
        <FormControl size="small" fullWidth>
          <InputLabel id="demo-multiple-checkbox-label">{formatMessage({ id: 'product.table.detail.tags' })}</InputLabel>
          <Select
            labelId="demo-multiple-checkbox-label"
            id="demo-multiple-checkbox"
            multiple
            value={categoryFilter.tags}
            onChange={e => { updateCategoryFilter(filter.name, e.target.value) }}
            input={<OutlinedInput label={formatMessage({ id: 'product.table.detail.tags' })} />}
            renderValue={(selected) => selected.join(', ')}
            MenuProps={MenuProps}
          >
            {filteredTags.map((tag) => (
              <MenuItem key={tag} value={tag}>
                <Checkbox checked={categoryFilter.tags.includes(tag)} />
                <ListItemText primary={tag} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    } else {
      return <Grid item key={filter.name} xs={3} sm={3} md={3}>
        <TextField
          select
          type="text"
          size="small"
          label={filter.text}
          variant="outlined"
          onChange={e => { updateCategoryFilter(filter.name, e.target.value) }}
          value={categoryFilter[filter.name] || ''}
          fullWidth
        >
          {getMenuItem(filter.name).map((option, idx) => {
            return option.header ?
              (<ListSubheader key={`${option.value}-${idx}`}>{option.label}</ListSubheader>) :
              (<MenuItem key={`${option.value}-${idx}`} value={option.value}>{option.label}</MenuItem>)
          })}
        </TextField>
      </Grid>
    }
  }

  return (
    <div style={{ paddingBottom: '100px' }}>
      <Toolbar sx={{ pl: 2, pr: 1 }}>
        <Typography sx={{ flex: '1 1 100%', flexShrink: 2 }} variant="h6" id="tableTitle" component="div">
          <FormattedMessage id="product.table.title" />
        </Typography>
        <Grid container spacing={3}>
          {categoryFilterItems.map(f => getFilter(f))}
        </Grid>
        <SearchBox filterItems={filterItems} onFilterChanged={onFilterChanged} />
      </Toolbar>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: '650px' }}>
          <EnhancedTableHead
            headerCells={headerCells}
            order={order}
            orderBy={orderBy}
            expandable
            onRequestSort={handleRequestSort}
            rowCount={filteredproducts.length}
          />
          <TableBody>
            {stableSort(filteredproducts, getComparator(order, orderBy)).map(product => (
              <EnhancedTableRow
                key={product.id}
                rowCells={rowCells}
                cellData={product}
                expandable
                expandContent={<ProductDetail
                  customerId={customerId}
                  allowEditing={!!currentUser.userRights['product-edit']}
                  customerMapping={customerMapping}
                  productMapping={productMapping}
                  product={formatData(product)}
                />}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

SyncProductView.propTypes = {
  customerId: PropTypes.string.isRequired,
  productMapping: PropTypes.object.isRequired
};

export default SyncProductView;
